import FeatureLayer from '@arcgis/core/layers/FeatureLayer.js';
import { buildPopupTemplate } from './popup';
import { buildRenderer, isColorableGeometry } from './renderer';
import { getLayerColor, type RGB } from './symbology';
import type { LayerProbe, LayerRecord } from './types';

const instances = new Map<string, FeatureLayer>();

// Tracks the last styling we applied so repeated calls (e.g., registry ticks
// on every probe-status change) don't rebuild identical PopupTemplate /
// SimpleRenderer instances and force ArcGIS to re-init layer state.
type LastApplied = {
  probeRef: LayerProbe | undefined;
  popupRef: __esri.PopupTemplate;
  rendererRef: __esri.Renderer | undefined;
  rendererColor: RGB | undefined;
  rendererGeometry: string | undefined;
};
const lastApplied = new Map<string, LastApplied>();

// Tiles default to fetching only OID + display field; the PopupTemplate sets its
// own outFields: ['*'] so opening a popup pulls full attributes on demand, and
// selection.ts re-queries the clicked OID when it needs the rest. Renderer-driven
// fields (uniqueValueInfos / classBreakInfos) are auto-included by the SDK.
function defaultOutFields(record: LayerRecord): string[] {
  const oid = record.probe?.service.object_id_field;
  const display = record.probe?.service.display_field;
  if (!oid && !display) return ['*'];
  const fields = [oid, display].filter((f): f is string => !!f);
  return Array.from(new Set(fields));
}

export function getOrCreateFeatureLayer(record: LayerRecord): FeatureLayer {
  const existing = instances.get(record.id);
  if (existing) return existing;

  // ArcGIS treats minScale=0 as "no minimum scale" (the default), so passing 0
  // when min_scale isn't configured is a no-op.
  const layer = new FeatureLayer({
    id: record.id,
    url: record.url,
    title: record.id,
    outFields: defaultOutFields(record),
    popupEnabled: true,
    popupTemplate: buildPopupTemplate(record),
    minScale: record.min_scale ?? 0,
  });
  instances.set(record.id, layer);
  return layer;
}

export function getFeatureLayer(id: string): FeatureLayer | undefined {
  return instances.get(id);
}

export function applyLayerStyling(record: LayerRecord, allRecords: LayerRecord[]): void {
  const layer = instances.get(record.id);
  if (!layer) return;
  const prev = lastApplied.get(record.id);

  const nextOutFields = defaultOutFields(record);
  if (!sameStringList(layer.outFields, nextOutFields)) {
    layer.outFields = nextOutFields;
  }

  // Rebuild popup only if the probe identity changed, or some other code path
  // replaced layer.popupTemplate behind our back.
  let popupRef = prev?.popupRef;
  if (!prev || prev.probeRef !== record.probe || layer.popupTemplate !== prev.popupRef) {
    popupRef = buildPopupTemplate(record);
    layer.popupTemplate = popupRef;
  }

  const geometryType =
    (layer.geometryType as string | undefined) ?? record.probe?.service.geometry_type;

  let rendererRef = prev?.rendererRef;
  let rendererColor = prev?.rendererColor;
  let rendererGeometry = prev?.rendererGeometry;

  if (geometryType && isColorableGeometry(geometryType)) {
    const color = getLayerColor(record.id, allRecords);
    const colorUnchanged =
      prev && sameRGB(prev.rendererColor, color) && prev.rendererGeometry === geometryType;
    const layerOwnsCachedRenderer = prev && layer.renderer === prev.rendererRef;
    if (!colorUnchanged || !layerOwnsCachedRenderer) {
      const renderer = buildRenderer(geometryType, color);
      if (renderer) {
        layer.renderer = renderer;
        rendererRef = renderer;
        rendererColor = color;
        rendererGeometry = geometryType;
      }
    }
  }

  if (popupRef) {
    lastApplied.set(record.id, {
      probeRef: record.probe,
      popupRef,
      rendererRef,
      rendererColor,
      rendererGeometry,
    });
  }
}

function sameStringList(a: string[] | null | undefined, b: string[]): boolean {
  if (!a || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function sameRGB(a: RGB | undefined, b: RGB): boolean {
  return !!a && a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}
